import { isMobile } from "./mobile";
import { Geolocation } from "@capacitor/geolocation";
import { getLastKnownPosition } from "./mobile-position-watcher";
import { getPositionFetchFromWatch, debugAlert } from "./get-position-from-watch-or-demand";

export interface Location {
  lat: number;
  lon: number;
  accuracy: number;
}

const getPosition = async (options: {
  timeout: number | null;
}): Promise<Location | null> => {
  if (isMobile) {
    try {
      // const watched = getLastKnownPosition();
      // if (watched) {
      //   return {
      //     lat: watched.coords.latitude,
      //     lon: watched.coords.longitude,
      //     accuracy: watched.coords.accuracy,
      //   };
      // }
      const coordinates = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        ...(options.timeout && { timeout: options.timeout }),
      });
      if (!coordinates) {
        return null;
      }
      return {
        lat: coordinates.coords.latitude,
        lon: coordinates.coords.longitude,
        accuracy: coordinates.coords.accuracy,
      };
    } catch (err) {
      console.error(
        "Error getting position from Geolocation.getCurrentPosition",
        err
      );
      return null;
    }
  }

  return getPositionForWeb(options);
};

export const getPositionFromWatch = async (options: {
  timeout: number | null;
}): Promise<Location | null> => {
  if (isMobile) {
    try {
      const shouldUseWatch = getPositionFetchFromWatch();
      debugAlert(`1. Feature toggle set to: ${shouldUseWatch}`);
      // console.log("## shouldUseWatch", shouldUseWatch);
      if (shouldUseWatch) {
        // console.log("## getting from watch");
        const watched = getLastKnownPosition();
        if (watched) {
          // console.log("## watched is set, using");
          debugAlert("2. Returning position from watch, not from GPS.");
          return {
            lat: watched.coords.latitude,
            lon: watched.coords.longitude,
            accuracy: watched.coords.accuracy,
          };
        }
      }
      // console.log("## getting from Geolocation main plugin");
      const startFetchDate = new Date();
      debugAlert(
        `3. No position in watch, fetching from GPS. Timeout set to ${options.timeout}`
      );

      const locationPromise = Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
      });

      const coordinates = options.timeout
        ? await Promise.race([
            locationPromise,
            new Promise<null>((resolve) => setTimeout(() => resolve(null), options.timeout!))
          ])
        : await locationPromise;
      const endFetchDate = new Date();
      const timeDiffInSeconds =
        (endFetchDate.getTime() - startFetchDate.getTime()) / 1000;
      debugAlert(`4. Fetch location took ${timeDiffInSeconds} seconds.`);
      if (!coordinates) {
        debugAlert(`5. Fetch location couldn't get coordinates.`);
        // console.log('### no coordinates, returning null')
        return null;
      }
      // console.log(`### returning coordinates with accuracy: ${coordinates.coords.accuracy}`);
      debugAlert(
        `6. Fetch location got coordinates, with accuracy: ${coordinates.coords.accuracy}`
      );
      return {
        lat: coordinates.coords.latitude,
        lon: coordinates.coords.longitude,
        accuracy: coordinates.coords.accuracy,
      };
    } catch (err) {
      debugAlert("7. Error getting position from Geolocation.getCurrentPosition.");
      console.error(
        "Error getting position from Geolocation.getCurrentPosition",
        err
      );
      return null;
    }
  }

  return getPositionForWeb(options);
};

const getPositionForWeb = async (options: {
  timeout: number | null;
}): Promise<Location | null> => {
  return new Promise((resolve) => {
    const geolocation = navigator.geolocation;
    if (!geolocation) {
      console.error("Geolocation API not available");
      return resolve(null);
    }
    geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        console.error("Web navigator geolocation error:", error);
        return resolve(null);
      },
      {
        enableHighAccuracy: true,
        ...(options.timeout && { timeout: options.timeout }),
      }
    );
  });
};

export const getPositionForDriverEventOnDemand = getPosition.bind(null, {
  timeout: 5000,
});

export const getPositionForDriverEventFromWatch = getPositionFromWatch.bind(
  null,
  {
    timeout: 5000,
  }
);
